import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { getSnapshot } from 'mobx-state-tree';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from 'ui-box';
import {
  Button,
  Checkbox,
  ControlGroup,
  defaultFills,
  Heading,
  majorScale,
  SideSheet,
  Spinner,
  useToast,
} from '../../npaper';
import { formatUnit } from '../../npaper/FormattedUnit';
import {
  IProduct,
  ISale,
  ISaleItem,
  ProductType,
  SaleStatus,
  SaleType,
  useStores,
} from '../../stores';
import { ProductSupplierSelect } from '../products/ProductSupplierSelect';

export interface ICreateOrderDialogProps {
  sale: ISale;
  isShown: boolean;
  onClose: () => void;
}

interface IProcureData {
  // The sale item to reserve
  item: ISaleItem;
  // Should we reserve the item?
  active: boolean;
  // Should we drop-ship?
  drop_ship: boolean;
}

export const CreateOrderDialog: React.FC<ICreateOrderDialogProps> = observer(
  ({ isShown, sale, onClose }) => {
    const toaster = useToast();
    const intl = useIntl();
    const { saleItemStore, saleStore } = useStores();
    const [items, setItems] = useState<ISaleItem[]>();

    const [procurements, setProcurements] = useState<IProcureData[]>();

    useEffect(() => {
      if (!isShown) return;
      let isSub = true;
      setItems(undefined);
      saleItemStore
        .getAll({
          filter: { sale: sale.id, product__type: ProductType.GOOD },
        })
        .then(({ items: lines }) => isSub && setItems(lines));
      return () => {
        isSub = false;
      };
    }, [sale, saleItemStore, isShown]);

    useEffect(() => {
      if (!items) return;
      setProcurements(
        items
          .filter((item) => item.quantity_missing > 0)
          .map((item) => ({
            item,
            active: true,
            drop_ship: false,
          }))
      );
    }, [items]);

    const readyToOrder =
      procurements && !!procurements.find((res) => res.active);

    const [placingOrder, setPlacingOrder] = useState(false);

    const placeOrder = async () => {
      if (!readyToOrder || !procurements) {
        return;
      }
      setPlacingOrder(true);
      try {
        await saleStore.procureItems(
          sale,
          procurements
            .filter((r) => r.active)
            .map((r) => ({ item: r.item.id, drop_ship: r.drop_ship }))
        );
        if (sale.status !== SaleStatus.OPEN)
          await saleStore.changeState(sale, SaleType.ORDER, SaleStatus.OPEN);

        onClose();
        toaster.addToast({
          message: intl.formatMessage({
            defaultMessage: 'Order was successfully created',
          }),
          intent: 'success',
        });
      } catch (err) {
        // TODO: Maybe we need more detailed error handling
        toaster.showDefaultError(err);
        return;
      } finally {
        setPlacingOrder(false);
      }
    };

    return (
      <SideSheet
        title={intl.formatMessage({
          defaultMessage: 'Procure and reserver order items',
        })}
        isShown={isShown}
        onClose={onClose}
        actions={
          <>
            <Button
              primary
              disabled={!readyToOrder}
              onClick={() => placeOrder()}
              isLoading={placingOrder}
            >
              <FormattedMessage defaultMessage="Procure and reserve items" />
            </Button>
          </>
        }
      >
        {procurements ? (
          <Box
            padding={majorScale(4)}
            width="100%"
            maxWidth="1024px"
            marginX="auto"
          >
            {procurements.length > 0 && (
              <>
                <Heading
                  size={300}
                  marginBottom={majorScale(1)}
                  marginTop={majorScale(4)}
                >
                  <FormattedMessage defaultMessage="Choose items to procure" />
                </Heading>

                <Box
                  display="grid"
                  gridTemplateColumns="40px minmax(200px, 1fr) 1fr 1fr"
                  alignItems="center"
                  padding={majorScale(1)}
                  paddingLeft={majorScale(2) + 2}
                  paddingBottom={0}
                >
                  <Box>
                    <Checkbox
                      checked={!procurements.find((r) => !r.active)}
                      onChange={() => {
                        if (procurements.find((r) => !r.active)) {
                          // Enable all
                          setProcurements(
                            procurements.map((res) => ({
                              ...res,
                              active: true,
                            }))
                          );
                        } else {
                          // Disable all
                          setProcurements(
                            procurements.map((res) => ({
                              ...res,
                              active: false,
                            }))
                          );
                        }
                      }}
                    />
                  </Box>
                  <Box>
                    <Heading size={200}>
                      <FormattedMessage defaultMessage="Item" />
                    </Heading>
                  </Box>
                  <Box>
                    <Heading size={200}>
                      <FormattedMessage defaultMessage="Summary" />
                    </Heading>
                  </Box>
                  <Box textAlign="right" paddingRight={majorScale(1)}>
                    <Heading size={200}>
                      <FormattedMessage defaultMessage="Supplier and delivery" />
                    </Heading>
                  </Box>
                </Box>

                <Box
                  backgroundColor="white"
                  overflow="hidden"
                  paddingBottom={1}
                >
                  {procurements.map((procurement, i) => (
                    <ItemRowRenderer
                      key={procurement.item.id}
                      procurement={procurement}
                      onChange={(newProcurement) => {
                        setProcurements((s) => {
                          const arr = Array.from(s || []);
                          const index = arr.indexOf(procurement);
                          if (index > -1) {
                            arr.splice(index, 1, newProcurement);
                          }
                          return arr;
                        });
                      }}
                    />
                  ))}
                </Box>
              </>
            )}
          </Box>
        ) : (
          <Box
            paddingTop={majorScale(6)}
            display="flex"
            justifyContent="center"
            width="100%"
          >
            <Spinner />
          </Box>
        )}
      </SideSheet>
    );
  }
);

const ItemRowRenderer = observer(
  ({
    procurement,
    onChange,
  }: {
    procurement: IProcureData;
    onChange: (reservation: IProcureData) => void;
  }) => {
    const toaster = useToast();
    const intl = useIntl();
    const { saleItemStore } = useStores();
    const item = procurement.item;

    const product = item.product as IProduct | undefined;

    const stockFromWarehouse = procurement.drop_ship
      ? 0
      : Math.min(
          item.quantity_missing,
          (item.product as IProduct).available_stock || 0
        );

    const stockFromSupplier = item.supplier_item
      ? item.quantity_missing - stockFromWarehouse
      : 0;

    const stockIsAvailable =
      product &&
      item.quantity_missing <= stockFromWarehouse + stockFromSupplier;

    const ready = product && item.supplier_item !== undefined;

    return (
      <Box
        display="grid"
        gridTemplateColumns="40px minmax(200px, 1fr) 1fr 1fr"
        alignItems="center"
        padding={majorScale(1)}
        paddingLeft={majorScale(2)}
        background={
          procurement.active
            ? !stockIsAvailable && procurement.active && ready
              ? defaultFills.danger[50]
              : null
            : defaultFills.normal[50]
        }
        boxShadow={
          !stockIsAvailable && procurement.active && ready
            ? `inset 4px 0 0 0 ${defaultFills.danger[500]}`
            : null
        }
        marginBottom={-1}
        border={
          !stockIsAvailable && procurement.active && ready
            ? `1px solid ${defaultFills.danger[500]}`
            : `1px solid ${defaultFills.normal[100]}`
        }
      >
        {product && item.supplier_item !== undefined ? (
          <>
            <Box>
              <Checkbox
                checked={procurement.active}
                onChange={(f) => onChange({ ...procurement, active: f })}
              />
            </Box>

            <Box paddingRight={majorScale(2)} maxWidth="100%">
              <Box is="p" maxWidth="100%" className="elipsis">
                {product.name}
              </Box>
              <p className="elipsis font-mono">{product.sku}</p>
              <p className="font-mono muted small">
                <FormattedMessage
                  defaultMessage="{qty} {unit} needed"
                  values={{
                    qty: item.quantity_missing,
                    unit: formatUnit(intl, product.unit),
                  }}
                />
              </p>
            </Box>
            <Box>
              <p className="font-mono muted small">
                <FormattedMessage
                  defaultMessage="{qty} {unit} from warehouse"
                  values={{
                    qty: stockFromWarehouse,
                    unit: formatUnit(intl, product.unit),
                  }}
                />
              </p>
              {item.supplier_item && item.supplier_item.supplier && (
                <p className="font-mono muted small">
                  <FormattedMessage
                    defaultMessage="{qty} {unit} from {supplier}"
                    values={{
                      qty: stockFromSupplier,
                      unit: formatUnit(intl, product.unit),
                      supplier: item.supplier_item.supplier.name,
                    }}
                  />
                </p>
              )}
              <Box
                is="p"
                className={classNames(
                  'font-mono small',
                  !stockIsAvailable && procurement.active && 'danger'
                )}
                textDecoration="underline"
              >
                <FormattedMessage
                  defaultMessage="{qty} {unit} in total"
                  values={{
                    qty: stockFromWarehouse + stockFromSupplier,
                    unit: formatUnit(intl, product.unit),
                  }}
                />
              </Box>
            </Box>

            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              paddingRight={majorScale(0.5)}
            >
              {item.supplier_item &&
                (item.supplier_item.supplier ? (
                  <Box textAlign="right">
                    <p className="elipsis">
                      {item.supplier_item.supplier.name}
                    </p>
                    <Box is="p" className="font-mono muted" whiteSpace="nowrap">
                      <FormattedMessage
                        defaultMessage="{qty} {unit} available"
                        values={{
                          qty: item.supplier_item.stock,
                          unit: formatUnit(intl, product.unit),
                        }}
                      />
                    </Box>
                  </Box>
                ) : (
                  <Spinner size={16} />
                ))}
              {!item.supplier_item && (
                <Box textAlign="right">
                  <p>
                    <FormattedMessage defaultMessage="Own warehouse" />
                  </p>
                  <Box
                    is="p"
                    className={classNames(
                      'font-mono muted',
                      !stockIsAvailable && procurement.active && 'danger'
                    )}
                    whiteSpace="nowrap"
                  >
                    <FormattedMessage
                      defaultMessage="{qty} {unit} available"
                      values={{
                        qty: product.available_stock || 0,
                        unit: formatUnit(intl, product.unit),
                      }}
                    />
                  </Box>
                </Box>
              )}
              {item.supplier_item ? (
                <Button
                  iconLeft="cross"
                  minimal
                  marginLeft="8px"
                  onClick={() => {
                    const old = item.supplier_item;
                    item.setSupplierItem(null);
                    saleItemStore
                      .updateItem(item, {
                        supplier_item: null,
                      })
                      .catch((err) => {
                        toaster.showDefaultError(err);
                        item.setSupplierItem(old);
                      });
                  }}
                />
              ) : (
                <ProductSupplierSelect
                  placement="bottom-end"
                  onSelectSupplierProductItem={(supplierItem) => {
                    saleItemStore
                      .updateItem(item, {
                        supplier_item: supplierItem.id,
                      })
                      .catch(toaster.showDefaultError);
                  }}
                  value={getSnapshot(item).supplier_item || undefined}
                  filter={{ product: product.id }}
                >
                  <Button minimal iconRight="truck" marginLeft="8px" />
                </ProductSupplierSelect>
              )}
              <ControlGroup marginLeft={majorScale(1)}>
                <Button
                  intent={procurement.drop_ship ? 'info' : 'none'}
                  primary={procurement.drop_ship}
                  onClick={() => onChange({ ...procurement, drop_ship: true })}
                >
                  <FormattedMessage defaultMessage="Drop ship" />
                </Button>
                <Button
                  primary={!procurement.drop_ship}
                  intent={!procurement.drop_ship ? 'info' : 'none'}
                  onClick={() => onChange({ ...procurement, drop_ship: false })}
                >
                  <FormattedMessage defaultMessage="Warehouse" />
                </Button>
              </ControlGroup>
            </Box>
          </>
        ) : (
          <Box
            height={40}
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="100%"
          >
            <Spinner size={16} />
          </Box>
        )}
      </Box>
    );
  }
);
